import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, TrendingUp, Users, RefreshCw } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';

// ------------------------------------------------------------------ //
// Types
// ------------------------------------------------------------------ //

interface DemoFunnel {
  created: number;
  shared: number;
  ctaClicked: number;
  oppAdvanced: number;
  createdToSharedPct: number;
  sharedToCtaPct: number;
  ctaToOppAdvancedPct: number;
}

interface DemoConversionByRep {
  repId: string;
  repEmail: string | null;
  demosSent: number;
  oppAdvanced: number;
  conversionRate: number;
}

interface FunnelFilters {
  from: string;
  to: string;
  rep: string;
  industry: string;
  theme: string;
}

// ------------------------------------------------------------------ //
// API helpers
// ------------------------------------------------------------------ //

async function fetchDemoAnalyticsFunnel(filters: Partial<FunnelFilters>): Promise<DemoFunnel> {
  const params: Record<string, string> = {};
  if (filters.from) params.from = filters.from;
  if (filters.to) params.to = filters.to;
  if (filters.rep) params.rep = filters.rep;
  if (filters.industry) params.industry = filters.industry;
  if (filters.theme) params.theme = filters.theme;
  const { data } = await apiClient.get<DemoFunnel>('/demos/analytics/funnel', { params });
  return data;
}

async function fetchDemoConversionByRep(): Promise<DemoConversionByRep[]> {
  const { data } = await apiClient.get<DemoConversionByRep[]>('/demos/analytics/conversion-by-rep');
  return data;
}

// ------------------------------------------------------------------ //
// Funnel bar component
// ------------------------------------------------------------------ //

function FunnelStageBar({
  label,
  count,
  pct,
  color,
  testId,
}: {
  label: string;
  count: number;
  pct?: number;
  color: string;
  testId?: string;
}) {
  return (
    <div className="space-y-1" data-testid={testId}>
      <div className="flex justify-between text-sm">
        <span className="font-medium">{label}</span>
        <span className="text-muted-foreground">
          {count}
          {pct !== undefined && (
            <span className="ml-2 text-xs">({pct.toFixed(1)}%)</span>
          )}
        </span>
      </div>
      <div className="h-3 w-full rounded bg-muted">
        <div
          className={`h-3 rounded ${color} transition-all`}
          style={{ width: pct !== undefined ? `${Math.min(pct, 100)}%` : '100%' }}
        />
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ //
// Main page component
// ------------------------------------------------------------------ //

/**
 * Ticket 114.7 - DemoAnalyticsPage.
 *
 * SALES_MANAGER view: funnel (CREATED to OPP_ADVANCED), filters
 * (date range, rep, industry, theme), and conversion-by-rep table.
 *
 * Gated by SALES_MANAGER role in the router (App.tsx).
 */
export function DemoAnalyticsPage() {
  const { t } = useTranslation();

  const [funnel, setFunnel] = useState<DemoFunnel | null>(null);
  const [convByRep, setConvByRep] = useState<DemoConversionByRep[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filters, setFilters] = useState<Partial<FunnelFilters>>({});
  const [pendingFilters, setPendingFilters] = useState<Partial<FunnelFilters>>({});

  function applyFilters() {
    setFilters(pendingFilters);
  }

  function resetFilters() {
    setPendingFilters({});
    setFilters({});
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([fetchDemoAnalyticsFunnel(filters), fetchDemoConversionByRep()])
      .then(([funnelData, repData]) => {
        if (cancelled) return;
        setFunnel(funnelData);
        setConvByRep(repData);
      })
      .catch(() => {
        if (!cancelled) setError(t('common.error'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [filters, t]);

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <header className="flex items-center gap-3">
        <BarChart3 className="h-5 w-5 text-muted-foreground" />
        <div>
          <h1 className="text-2xl font-semibold" data-testid="demo-analytics-title">
            {t('demos.analytics.title')}
          </h1>
          <p className="text-sm text-muted-foreground">{t('demos.analytics.funnel')}</p>
        </div>
      </header>

      {/* Filter bar */}
      <div className="rounded-lg border bg-card p-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              {t('demos.analytics.filter.from')}
            </label>
            <input
              type="date"
              className="w-full rounded border bg-background px-2 py-1 text-sm"
              value={pendingFilters.from || ''}
              onChange={(e) => setPendingFilters((f) => ({ ...f, from: e.target.value || undefined }))}
              data-testid="filter-from"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              {t('demos.analytics.filter.to')}
            </label>
            <input
              type="date"
              className="w-full rounded border bg-background px-2 py-1 text-sm"
              value={pendingFilters.to || ''}
              onChange={(e) => setPendingFilters((f) => ({ ...f, to: e.target.value || undefined }))}
              data-testid="filter-to"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              {t('demos.analytics.filter.industry')}
            </label>
            <input
              type="text"
              className="w-full rounded border bg-background px-2 py-1 text-sm"
              placeholder="restaurants"
              value={pendingFilters.industry || ''}
              onChange={(e) => setPendingFilters((f) => ({ ...f, industry: e.target.value || undefined }))}
              data-testid="filter-industry"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              {t('demos.analytics.filter.theme')}
            </label>
            <select
              className="w-full rounded border bg-background px-2 py-1 text-sm"
              value={pendingFilters.theme || ''}
              onChange={(e) => setPendingFilters((f) => ({ ...f, theme: e.target.value || undefined }))}
              data-testid="filter-theme"
            >
              <option value="">—</option>
              <option value="sunburst">Sunburst</option>
              <option value="editorial">Editorial</option>
              <option value="quantum">Quantum</option>
              <option value="aurora">Aurora</option>
            </select>
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={applyFilters}
              className="rounded bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
              data-testid="filter-apply"
            >
              {t('demos.analytics.filter.apply')}
            </button>
            <button
              onClick={resetFilters}
              className="rounded border px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted"
              data-testid="filter-reset"
            >
              {t('demos.analytics.filter.reset')}
            </button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <RefreshCw className="h-4 w-4 animate-spin" />
          {t('common.loading')}
        </div>
      )}

      {error && !loading && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Funnel section */}
      {!loading && funnel && (
        <div className="rounded-lg border bg-card p-6" data-testid="funnel-section">
          <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            <TrendingUp className="h-4 w-4" />
            {t('demos.analytics.funnel')}
          </h2>

          <div className="space-y-4">
            <FunnelStageBar
              label={t('demos.analytics.stage.created')}
              count={funnel.created}
              color="bg-blue-500"
              testId="stage-created"
            />
            <FunnelStageBar
              label={t('demos.analytics.stage.shared')}
              count={funnel.shared}
              pct={funnel.createdToSharedPct}
              color="bg-cyan-500"
              testId="stage-shared"
            />
            <FunnelStageBar
              label={t('demos.analytics.stage.ctaClicked')}
              count={funnel.ctaClicked}
              pct={funnel.sharedToCtaPct}
              color="bg-amber-500"
              testId="stage-cta-clicked"
            />
            <FunnelStageBar
              label={t('demos.analytics.stage.oppAdvanced')}
              count={funnel.oppAdvanced}
              pct={funnel.ctaToOppAdvancedPct}
              color="bg-green-500"
              testId="stage-opp-advanced"
            />
          </div>
        </div>
      )}

      {/* Conversion by rep section */}
      {!loading && convByRep.length > 0 && (
        <div className="rounded-lg border bg-card p-6" data-testid="conversion-by-rep-section">
          <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            <Users className="h-4 w-4" />
            {t('demos.analytics.conversionByRep')}
          </h2>

          <div className="overflow-hidden rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">{t('demos.analytics.filter.rep')}</th>
                  <th className="px-3 py-2 text-right">{t('demos.analytics.stage.shared')}</th>
                  <th className="px-3 py-2 text-right">{t('demos.analytics.stage.oppAdvanced')}</th>
                  <th className="px-3 py-2 text-right">{t('demos.analytics.conversionByRep')}</th>
                </tr>
              </thead>
              <tbody>
                {convByRep.map((row) => (
                  <tr key={row.repId} className="border-t">
                    <td className="px-3 py-2 font-medium">{row.repEmail ?? row.repId}</td>
                    <td className="px-3 py-2 text-right">{row.demosSent}</td>
                    <td className="px-3 py-2 text-right">{row.oppAdvanced}</td>
                    <td className="px-3 py-2 text-right font-semibold">{Number(row.conversionRate).toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && !error && convByRep.length === 0 && funnel !== null && (
        <div className="rounded-lg border bg-card p-6 text-sm text-muted-foreground">
          {t('demos.analytics.conversionByRep')} — {t('common.empty_state', { defaultValue: 'No data yet.' })}
        </div>
      )}
    </div>
  );
}
